import { useMemo, useState } from 'react'
import { isAxiosError } from 'axios'
import { CheckCircle2, Phone, PiggyBank, Send } from 'lucide-react'

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Chip, ChipGroup } from '@/components/ui/chip'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

import { inrFull, localDate, localDateOffset } from '@/lib/format'

import { useMe } from '@/features/auth/api'
import { navPermissionCheck } from '@/components/layout/navVisibility'
import {
  NOTE_REQUIRED_OUTCOMES,
  useCaptureDisposition,
  useClearDeceasedReport,
  useConductStatus,
  useOpenPtp,
  type ConductOverrideInput,
  type ConductStatus,
  type ContactResult,
  type DispositionRequest,
  type Outcome,
} from './api'
import { CONTACT_RESULTS, OUTCOMES } from './dispositionLabels'

/**
 * The single action surface on a case. Two flows:
 *  - Capture a disposition (and, if outcome is PROMISE_TO_PAY / PARTIAL_PROMISE,
 *    open a PTP inline in the same transaction).
 *  - Open a standalone PTP without a disposition (manager backfill).
 *
 * Voice rule: lender-domain. "Promised amount", "Promise date", not "ETA".
 */

type Mode = 'disposition' | 'ptp'

export function CaseActionsPanel({ caseId, currentOutstanding }: {
  caseId: string
  currentOutstanding?: string | null
}) {
  const [mode, setMode] = useState<Mode>('disposition')

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle>Capture action</CardTitle>
            <CardDescription>
              Disposition appends to the activity timeline. PTPs append AND start the state machine.
            </CardDescription>
          </div>
        </div>
        <div className="mt-3">
          <ChipGroup>
            <Chip active={mode === 'disposition'} onClick={() => setMode('disposition')}>
              Disposition
            </Chip>
            <Chip active={mode === 'ptp'} onClick={() => setMode('ptp')}>
              PTP only
            </Chip>
          </ChipGroup>
        </div>
      </CardHeader>
      <CardContent>
        {mode === 'disposition' ? (
          <DispositionForm caseId={caseId} suggestedAmount={currentOutstanding} />
        ) : (
          <PtpForm caseId={caseId} suggestedAmount={currentOutstanding} />
        )}
      </CardContent>
    </Card>
  )
}

// ---------------------------------------------------------------------------
// Disposition form
// ---------------------------------------------------------------------------

function DispositionForm({ caseId, suggestedAmount }: {
  caseId: string
  suggestedAmount: string | null | undefined
}) {
  const capture = useCaptureDisposition(caseId)
  const { data: conduct } = useConductStatus(caseId)

  const [contactResult, setContactResult] = useState<ContactResult | null>(null)
  const [outcome, setOutcome] = useState<Outcome | ''>('')
  const [notes, setNotes] = useState('')

  // PTP inline fields — only shown when outcome is a promise.
  const [promisedAmount, setPromisedAmount] = useState('')
  const [promisedDate, setPromisedDate] = useState(defaultPromiseDate())
  const [graceDays, setGraceDays] = useState('1')

  // Manager overrides — one per active block. Keyed by block code so the
  // panel can render them inline. The server validates that the actor
  // holds the matching *_OVERRIDE permission; if not, the 403 falls
  // through to the existing inline error.
  const [overrides, setOverrides] = useState<ConductOverrideInput[]>([])

  const needsOutcome = contactResult === 'CONNECTED'
  const promise = useMemo(() => isPromiseOutcome(outcome), [outcome])
  const activeBlocks = conduct?.enforced ? conduct.blocks : []
  const conductBlocked = activeBlocks.length > 0
  const allOverridden =
    !conductBlocked ||
    activeBlocks.every((b) => overrides.some((o) => o.code === b.code))
  const needsNote = NOTE_REQUIRED_OUTCOMES.has(outcome)
  const canSubmit =
    !!contactResult && (!needsOutcome || !!outcome) &&
    (!promise || !!promisedAmount) && (!conductBlocked || allOverridden) &&
    (!needsNote || !!notes.trim())

  function reset() {
    setContactResult(null); setOutcome(''); setNotes('')
    setPromisedAmount(''); setPromisedDate(defaultPromiseDate()); setGraceDays('1')
    setOverrides([])
  }

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!contactResult) return
    const body: DispositionRequest = {
      // No channel: the server derives it from this user's allocation slot.
      // Hard-coding 'TELE' here recorded every field agent working from web
      // as a phone call.
      contactResult,
      outcome: outcome || null,
      notes: notes.trim() || null,
      ptp: promise && promisedAmount
        ? {
            promisedAmount,
            promisedDate,
            graceDays: Number(graceDays) || 0,
            notes: notes.trim() || undefined,
          }
        : null,
      overrides: overrides.length > 0 ? overrides : null,
    }
    capture.mutate(body, { onSuccess: reset })
  }

  const errorMessage = capture.isError
    ? isAxiosError(capture.error) && capture.error.response?.status === 409
      ? capture.error.response.data?.message ?? capture.error.response.data?.detail ?? 'Conflict — see details'
      : isAxiosError(capture.error) && capture.error.response?.status === 403
      ? conductCodeFromError(capture.error.response.data) ?? 'You do not have DISPOSITION_RECORD permission.'
      : 'Capture failed.'
    : null

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      {conduct && conduct.enforced && conduct.blocks.length > 0 && (
        <>
          <ConductBanner conduct={conduct} />
          {conduct.deceasedReportedAt && <ClearDeathReport caseId={caseId} />}
          <ConductOverridePanel
            blocks={conduct.blocks}
            overrides={overrides}
            onChange={setOverrides}
          />
        </>
      )}

      <FormField label="Contact result">
        <ChipGroup>
          {CONTACT_RESULTS.map((c) => (
            <Chip key={c.value} active={contactResult === c.value} onClick={() => {
              setContactResult(c.value)
              if (c.value !== 'CONNECTED') setOutcome('')
            }}>
              {c.label}
            </Chip>
          ))}
        </ChipGroup>
      </FormField>

      {needsOutcome && (
        <FormField label="Outcome" hint="Required when connected">
          <select
            value={outcome}
            onChange={(e) => setOutcome(e.target.value as Outcome)}
            required
            className="flex h-9 w-full rounded-md border border-slate-200 bg-white px-3 text-sm text-navy-900 hover:border-navy-200 focus-visible:outline-none focus-visible:border-navy-500 focus-visible:shadow-glow"
          >
            <option value="" disabled>Pick an outcome…</option>
            {OUTCOMES.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </FormField>
      )}

      {promise && (
        <div className="rounded-md border border-navy-200 bg-navy-50/60 p-3 space-y-3">
          <div className="flex items-center gap-2 text-2xs font-semibold uppercase tracking-wider text-navy-700">
            <PiggyBank className="h-3 w-3" />
            Promise to pay
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <FormField label="Promised amount" hint={suggestedAmount ? `Outstanding ${inrFull(suggestedAmount)}` : undefined}>
              <Input
                type="number"
                inputMode="decimal"
                min="1"
                step="0.01"
                value={promisedAmount}
                onChange={(e) => setPromisedAmount(e.target.value)}
                placeholder="12500.00"
                className="font-mono tabular-nums"
              />
            </FormField>
            <FormField label="Promise date">
              <Input
                type="date"
                value={promisedDate}
                min={today()}
                onChange={(e) => setPromisedDate(e.target.value)}
              />
            </FormField>
          </div>
          <FormField label="Grace days" hint="Days of slack before the PTP is marked BROKEN by the sweep">
            <Input
              type="number"
              min="0"
              max="14"
              value={graceDays}
              onChange={(e) => setGraceDays(e.target.value)}
              className="w-24 font-mono tabular-nums"
            />
          </FormField>
        </div>
      )}

      <FormField label="Notes" hint={needsNote ? 'Required — what the customer said' : 'Optional · max 1024 chars'}>
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Customer agreed to pay by Friday"
          maxLength={1024}
        />
      </FormField>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>Could not capture</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      {capture.isSuccess && capture.data && (
        <Alert variant="positive">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Captured</AlertTitle>
          <AlertDescription>
            Disposition recorded{capture.data.ptpId ? ' · PTP opened' : ''}.
          </AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <span className="text-2xs uppercase tracking-wider text-muted-foreground">
          {conductBlocked && !allOverridden
            ? 'Blocked by conduct policy'
            : (contactResult === 'CONNECTED' ? 'Connect + outcome' : 'Attempt')}
        </span>
        <Button
          type="submit"
          disabled={!canSubmit || capture.isPending}
          title={
            conductBlocked && !allOverridden
              ? 'Apply an override per active block, or sign in as a manager.'
              : undefined
          }
        >
          <Phone className="h-3.5 w-3.5" />
          {capture.isPending
            ? 'Saving…'
            : overrides.length > 0
            ? 'Save with override'
            : 'Save disposition'}
        </Button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Conduct override panel — one row per active block.
//
// We don't gate this client-side on roles: the JWT doesn't list raw
// permissions, and a hard-coded role→perm table would drift from the
// server. Everyone sees Override; the server's 403 with code +
// requiredPermission is the source of truth and lands in the existing
// inline error alert when the user lacks the matching perm.
// ---------------------------------------------------------------------------

function ConductOverridePanel({
  blocks,
  overrides,
  onChange,
}: {
  blocks: ConductStatus['blocks']
  overrides: ConductOverrideInput[]
  onChange: (next: ConductOverrideInput[]) => void
}) {
  return (
    <div className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
      <div className="flex items-center gap-1.5">
        <span className="text-2xs font-semibold uppercase tracking-wider text-navy-700">
          Manager override
        </span>
        <span className="text-2xs text-muted-foreground">
          · audit-logged per block
        </span>
      </div>
      {blocks.map((b) => {
        const applied = overrides.find((o) => o.code === b.code)
        return (
          <OverrideRow
            key={b.code}
            code={b.code}
            label={labelForBlock(b.code)}
            requiredPermission={b.requiredPermission}
            applied={applied}
            onApply={(reason) =>
              onChange([
                ...overrides.filter((o) => o.code !== b.code),
                { code: b.code, reason },
              ])
            }
            onRemove={() =>
              onChange(overrides.filter((o) => o.code !== b.code))
            }
          />
        )
      })}
    </div>
  )
}

function OverrideRow({
  code,
  label,
  requiredPermission,
  applied,
  onApply,
  onRemove,
}: {
  code: string
  label: string
  requiredPermission: string
  applied: ConductOverrideInput | undefined
  onApply: (reason: string) => void
  onRemove: () => void
}) {
  const [editing, setEditing] = useState(false)
  const [reason, setReason] = useState('')

  if (applied) {
    return (
      <div className="flex items-start justify-between gap-2 rounded border border-positive/30 bg-positive-soft px-2 py-1.5">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-navy-900">{label}</p>
          <p className="truncate text-2xs text-muted-foreground">
            Reason: {applied.reason}
          </p>
        </div>
        <button
          type="button"
          onClick={onRemove}
          className="text-2xs font-semibold uppercase tracking-wider text-negative hover:underline"
        >
          Remove
        </button>
      </div>
    )
  }

  if (editing) {
    return (
      <div className="space-y-1.5 rounded border border-slate-200 px-2 py-1.5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium text-navy-900">{label}</p>
          <span className="text-2xs text-muted-foreground">
            Needs {requiredPermission}
          </span>
        </div>
        <input
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why this override? (audit-logged)"
          maxLength={240}
          className="block h-8 w-full rounded-md border border-slate-200 bg-white px-2 text-xs text-navy-900 focus-visible:outline-none focus-visible:border-navy-500"
        />
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => { setEditing(false); setReason('') }}
            className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground hover:underline"
          >
            Cancel
          </button>
          <Button
            type="button"
            size="sm"
            disabled={!reason.trim()}
            onClick={() => { onApply(reason.trim()); setEditing(false); setReason('') }}
          >
            Apply override
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-between gap-2 rounded border border-slate-200 px-2 py-1.5">
      <p className="text-xs font-medium text-navy-900">{label}</p>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="text-2xs font-semibold uppercase tracking-wider text-navy-700 hover:underline"
        data-block={code}
      >
        Override
      </button>
    </div>
  )
}

function labelForBlock(code: string): string {
  switch (code) {
    case 'dnc_blocked':                return 'DNC'
    case 'contact_hours_blocked':      return 'Outside contact hours'
    case 'contact_frequency_exceeded': return 'Frequency cap reached'
    case 'deceased_reported':          return 'Death reported'
    default:                           return code
  }
}

// ---------------------------------------------------------------------------
// Conduct banner — surfaces DNC / hours / frequency blocks before the agent
// tries to capture.
// ---------------------------------------------------------------------------

/**
 * A manager who has checked a reported death (wrong customer, or the family
 * asks to be dealt with) clears it so contact can resume. Shown only to
 * holders of DNC_CONTACT_OVERRIDE; the server also checks the case is theirs.
 */
function ClearDeathReport({ caseId }: { caseId: string }) {
  const { data: me } = useMe()
  const clear = useClearDeceasedReport(caseId)
  const [reason, setReason] = useState('')
  if (!navPermissionCheck(me)('DNC_CONTACT_OVERRIDE')) return null
  return (
    <div className="space-y-2 rounded-md border border-slate-200 bg-white p-3">
      <span className="text-2xs font-semibold uppercase tracking-wider text-navy-700">Clear death report</span>
      <Textarea
        rows={2}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="What you checked — e.g. verified alive at branch"
      />
      {clear.isError && (
        <p className="text-xs text-negative">
          {isAxiosError(clear.error)
            ? String((clear.error.response?.data as { message?: string } | undefined)?.message ?? 'Could not clear it.')
            : 'Could not clear it.'}
        </p>
      )}
      <Button type="button" variant="outline" size="sm"
              disabled={!reason.trim() || clear.isPending}
              onClick={() => clear.mutate(reason.trim())}>
        Clear and allow contact
      </Button>
    </div>
  )
}

function ConductBanner({ conduct }: { conduct: ConductStatus }) {
  return (
    <Alert variant="warning">
      <AlertTitle>Capture blocked by conduct policy</AlertTitle>
      <AlertDescription>
        <ul className="mt-1 space-y-1">
          {conduct.blocks.map((b) => (
            <li key={b.code}>
              <span className="font-medium">{labelForBlock(b.code)}:</span>{' '}
              <span>{b.message}</span>
            </li>
          ))}
        </ul>
        <p className="mt-2 text-2xs uppercase tracking-wider text-muted-foreground">
          Contact window: {conduct.contactHoursStart}–{conduct.contactHoursEnd}{' '}
          {conduct.contactZone} · Frequency cap: {conduct.frequencyCap}/day ·
          Today's count: {conduct.contactCountToday}
        </p>
      </AlertDescription>
    </Alert>
  )
}

/**
 * The server returns 403 with {code, message, overridable, requiredPermission}
 * for conduct blocks, vs an opaque permission error for missing role.
 * Pull the message out when the shape matches.
 */
function conductCodeFromError(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null
  const code = (body as { code?: string }).code
  const msg = (body as { message?: string }).message
  if (typeof code !== 'string') return null
  if (code === 'dnc_blocked' || code === 'contact_hours_blocked' || code === 'contact_frequency_exceeded') {
    return msg ?? code
  }
  return null
}

// ---------------------------------------------------------------------------
// Standalone PTP form
// ---------------------------------------------------------------------------

function PtpForm({ caseId, suggestedAmount }: {
  caseId: string
  suggestedAmount: string | null | undefined
}) {
  const open = useOpenPtp(caseId)

  const [promisedAmount, setPromisedAmount] = useState('')
  const [promisedDate, setPromisedDate] = useState(defaultPromiseDate())
  const [graceDays, setGraceDays] = useState('1')
  const [notes, setNotes] = useState('')

  const canSubmit = !!promisedAmount && !!promisedDate

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    open.mutate(
      {
        promisedAmount,
        promisedDate,
        graceDays: Number(graceDays) || 0,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          setPromisedAmount(''); setPromisedDate(defaultPromiseDate())
          setGraceDays('1'); setNotes('')
        },
      },
    )
  }

  const errorMessage = open.isError
    ? isAxiosError(open.error) && open.error.response?.status === 409
      ? open.error.response.data?.message ?? 'Case already has an OPEN PTP'
      : isAxiosError(open.error) && open.error.response?.status === 403
      ? 'You do not have PTP_CAPTURE permission.'
      : 'Open PTP failed.'
    : null

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField label="Promised amount" hint={suggestedAmount ? `Outstanding ${inrFull(suggestedAmount)}` : undefined}>
          <Input
            type="number"
            inputMode="decimal"
            min="1"
            step="0.01"
            value={promisedAmount}
            onChange={(e) => setPromisedAmount(e.target.value)}
            placeholder="12500.00"
            className="font-mono tabular-nums"
            required
          />
        </FormField>
        <FormField label="Promise date">
          <Input
            type="date"
            value={promisedDate}
            min={today()}
            onChange={(e) => setPromisedDate(e.target.value)}
            required
          />
        </FormField>
      </div>
      <FormField label="Grace days">
        <Input
          type="number"
          min="0"
          max="14"
          value={graceDays}
          onChange={(e) => setGraceDays(e.target.value)}
          className="w-24 font-mono tabular-nums"
        />
      </FormField>
      <FormField label="Notes" hint="Optional">
        <Textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          maxLength={1024}
          placeholder="Backfill from yesterday's call sheet"
        />
      </FormField>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>Could not open PTP</AlertTitle>
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      {open.isSuccess && (
        <Alert variant="positive">
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>PTP opened</AlertTitle>
          <AlertDescription>The PTP is now OPEN and tracked by the sweep.</AlertDescription>
        </Alert>
      )}

      <div className="flex items-center justify-between">
        <span className="text-2xs uppercase tracking-wider text-muted-foreground">
          Channel: manual
        </span>
        <Button type="submit" disabled={!canSubmit || open.isPending}>
          <Send className="h-3.5 w-3.5" />
          {open.isPending ? 'Opening…' : 'Open PTP'}
        </Button>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isPromiseOutcome(o: Outcome | ''): boolean {
  return o === 'PROMISE_TO_PAY' || o === 'PARTIAL_PROMISE'
}

function today(): string {
  return localDate()
}

function defaultPromiseDate(): string {
  return localDateOffset(3)
}

function FormField({ label, hint, children }: { label: string; hint?: string | undefined; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label className="flex items-center justify-between">
        <span>{label}</span>
        {hint && <span className="text-2xs uppercase tracking-wider text-muted-foreground">{hint}</span>}
      </Label>
      {children}
    </div>
  )
}
